// SPDX-License-Identifier: AGPL-3.0-or-later
import {getProtectedIndexedDB} from '@app/features/platform/state/ProtectedWebStorage';

export const CHAT_CACHE_MAX_AGE = 24 * 60 * 60 * 1000;
export const CHAT_CACHE_CHANNEL_LIMIT = 30;
export const CHAT_CACHE_MESSAGE_LIMIT = 50;

export interface CachedChatPage {
	channelId: string;
	savedAt: number;
	scroll?: {scrollTop: number; scrollHeight: number; offsetHeight: number};
	messages: Array<import('@fluxer/schema/src/domains/message/MessageResponseSchemas').Message>;
}

export function isFreshChatPage(page: CachedChatPage, now = Date.now()): boolean {
	return (
		page != null &&
		typeof page.channelId === 'string' &&
		Number.isFinite(page.savedAt) &&
		page.savedAt <= now &&
		now - page.savedAt < CHAT_CACHE_MAX_AGE &&
		Array.isArray(page.messages) &&
		page.messages.length <= CHAT_CACHE_MESSAGE_LIMIT &&
		page.messages.every(
			(message) =>
				message != null && message.channel_id === page.channelId && typeof message.id === 'string' && !!message.author,
		)
	);
}

// Cache failures must never prevent login or message delivery. Each record belongs to an
// API instance and account; tokens are never used as keys or written into the cache.
export async function chatCacheRead<T>(key: string): Promise<T | undefined> {
	return access<T>(key);
}
export async function chatCacheWrite(key: string, value: unknown): Promise<void> {
	try {
		const serialized = JSON.stringify(value);
		if (new TextEncoder().encode(serialized).byteLength > 8 * 1024 * 1024) return;
		await access(key, JSON.parse(serialized), 'put');
	} catch {
		/* Invalid cache data is disposable. */
	}
}
export async function chatCacheDelete(key: string): Promise<void> {
	await access(key, undefined, 'delete');
}
async function access<T>(
	key: string,
	value?: unknown,
	operation: 'get' | 'put' | 'delete' = 'get',
): Promise<T | undefined> {
	try {
		const factory = getProtectedIndexedDB();
		if (!factory) return undefined;
		return await new Promise<T | undefined>((resolve) => {
			const open = factory.open('konvo-chat-cache', 1);
			let settled = false;
			const finish = (result?: T) => {
				if (!settled) {
					settled = true;
					resolve(result);
				}
			};
			open.onupgradeneeded = () => open.result.createObjectStore('snapshots');
			open.onerror = () => finish();
			open.onblocked = () => finish();
			open.onsuccess = () => {
				const db = open.result;
				if (settled) {
					db.close();
					return;
				}
				db.onversionchange = () => db.close();
				try {
					const tx = db.transaction('snapshots', operation === 'get' ? 'readonly' : 'readwrite');
					const store = tx.objectStore('snapshots');
					const request =
						operation === 'get' ? store.get(key) : operation === 'put' ? store.put(value, key) : store.delete(key);
					tx.oncomplete = () => {
						db.close();
						finish(operation === 'get' ? (request.result as T) : undefined);
					};
					tx.onabort = tx.onerror = () => {
						db.close();
						finish();
					};
				} catch {
					db.close();
					finish();
				}
			};
		});
	} catch {
		return undefined;
	}
}
